/**
 * Send a one-time code (README §5). Idea #3: OTP goes only to the
 * destination of record on the Halo contact — never a handle the requester
 * supplied, never free text a dispatcher types. Destinations render from
 * `modalReads.otpDestinations` (a real, injectable read — see ./reads.ts),
 * as clickable options only; there is no text input anywhere in this modal
 * that could carry a destination.
 *
 * Below Matched there is nowhere of record to send a code to (§3: "requires
 * a Matched contact"), so this modal shows that warning instead of any
 * destinations when the active thread's tier hasn't reached Matched or has
 * no `contactId`. The gate that actually enforces this lives in
 * services/ops (`otp-needs-matched-contact` / `otp-destination-not-of-record`
 * in packages/shared/src/gates.ts) — this UI check is a courtesy, and a
 * refusal from the layer is still surfaced via the `OpsError` catch below,
 * never swallowed.
 *
 * Stage two is the read-back: a 6-digit field, Verify. `verify-otp` returns
 * `{ verified, thread }` (services/ops/src/routes/operations.ts) — a
 * verified read-back's `thread.tier` comes back `'otp'`
 * (services/ops's `setTier`, the sole writer of `thread.tier`), and this
 * modal pushes that into the store via `upsertThread` so the OTP-Verified
 * tier is visible everywhere immediately, then closes.
 */
import { useEffect, useState } from 'react'
import type { ThreadSummary, Tier } from '@communicator/shared'
import { Button, Modal } from '../ui'
import { ops, OpsError } from '../../lib/ops'
import { useStore } from '../../store'
import { modalReads, OTP_DESTINATIONS_SEAM, type OtpDestination, type OtpDestinationsResult } from './reads'
import { GENERIC_OP_ERROR, NO_DESTINATIONS_TEXT, OTP_NEEDS_MATCHED_TEXT, unwiredText } from './format'

const MATCHED_OR_ABOVE: ReadonlySet<Tier> = new Set<Tier>(['matched', 'otp', 'verified'])

export function OtpModal() {
  const modal = useStore((s) => s.modal)
  const activeId = useStore((s) => s.activeId)
  const threads = useStore((s) => s.threads)
  const otpSent = useStore((s) => s.otpSent)
  const otpCode = useStore((s) => s.otpCode)
  const otpMethod = useStore((s) => s.otpMethod)
  const set = useStore((s) => s.set)
  const closeModal = useStore((s) => s.closeModal)
  const upsertThread = useStore((s) => s.upsertThread)

  const open = modal === 'otp'
  const thread = threads.find((t) => t.id === activeId) ?? null
  // haloContactId: the contact of record in Halo, which is where a code can be
  // sent. contactId is the client's own handle and is null on an SMS thread
  // even after matching (migration 003).
  const eligible = thread !== null && thread.haloContactId !== null && MATCHED_OR_ABOVE.has(thread.tier)

  const [result, setResult] = useState<OtpDestinationsResult>(OTP_DESTINATIONS_SEAM)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [mismatch, setMismatch] = useState(false)

  useEffect(() => {
    if (!open || !activeId || !eligible) return
    let cancelled = false
    modalReads.otpDestinations(activeId).then((r) => {
      if (!cancelled) setResult(r)
    })
    return () => {
      cancelled = true
    }
  }, [open, activeId, eligible])

  useEffect(() => {
    if (!open) {
      setError(null)
      setMismatch(false)
      setResult(OTP_DESTINATIONS_SEAM)
    }
  }, [open])

  if (!open) return null

  function handleModalClose() {
    set({ otpSent: false, otpCode: '', otpMethod: null })
    closeModal()
  }

  async function handleSend(dest: OtpDestination) {
    if (!activeId) return
    setBusy(true)
    setError(null)
    try {
      await ops.call('request-otp', { threadId: activeId, method: dest.method, destination: dest.destination })
      set({ otpSent: true, otpMethod: dest.method })
    } catch (e) {
      setError(e instanceof OpsError ? e.message : GENERIC_OP_ERROR)
    } finally {
      setBusy(false)
    }
  }

  async function handleVerify() {
    if (!activeId) return
    setBusy(true)
    setError(null)
    setMismatch(false)
    try {
      const result = (await ops.call('verify-otp', { threadId: activeId, code: otpCode })) as {
        verified: boolean
        thread: ThreadSummary
      }
      upsertThread(result.thread)
      if (result.verified) {
        set({ otpSent: false, otpCode: '', otpMethod: null })
        closeModal()
      } else {
        setMismatch(true)
      }
    } catch (e) {
      setError(e instanceof OpsError ? e.message : GENERIC_OP_ERROR)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal open={open} title="Send a one-time code" onClose={handleModalClose}>
      {!eligible && (
        <p data-testid="otp-modal-needs-match" className="text-[13px] text-warn">
          {OTP_NEEDS_MATCHED_TEXT}
        </p>
      )}

      {eligible && error && (
        <p
          data-testid="otp-modal-error"
          role="alert"
          className="mb-3 rounded-[8px] bg-bad-subtle px-3 py-2 text-[12px] text-bad"
        >
          {error}
        </p>
      )}

      {eligible && !otpSent && (
        <div data-testid="otp-modal-destinations">
          <p className="mb-2 text-[13px] text-mid">Send a code to the destination of record:</p>
          {!result.wired ? (
            <p data-testid="otp-modal-unreachable" className="text-[13px] text-warn">
              {unwiredText('destinations', result.reason)}
            </p>
          ) : result.destinations.length === 0 ? (
            <p data-testid="otp-modal-no-destinations" className="text-[13px] text-lo">
              {NO_DESTINATIONS_TEXT}
            </p>
          ) : (
            /* Every destination stays on the list, each saying what it would
               actually prove. Hiding the circular one would be the system
               making a judgement it cannot make: a dispatcher may know
               something about this caller that we do not. Explaining it, and
               ordering the useful ones first, lets them choose knowingly. */
            result.destinations.map((d) => (
              <div
                key={`${d.method}-${d.destination}`}
                className="mb-2 rounded-[8px] border border-subtle bg-card p-2.5"
              >
                <div className="mb-1 flex items-center gap-2">
                  <span className="text-[13px] text-hi">{d.label}</span>
                  {d.strength === 'circular' ? (
                    <span
                      data-testid={`otp-modal-strength-${d.method}`}
                      className="rounded-[20px] bg-warn-subtle px-2 py-0.5 text-[10px] font-semibold tracking-[.06em] text-warn"
                    >
                      PROVES NOTHING NEW
                    </span>
                  ) : d.strength === 'anti-spoof' ? (
                    <span
                      data-testid={`otp-modal-strength-${d.method}`}
                      className="rounded-[20px] bg-info-subtle px-2 py-0.5 text-[10px] font-semibold tracking-[.06em] text-info"
                    >
                      DEFEATS CALLER-ID SPOOFING
                    </span>
                  ) : null}
                </div>
                {d.strengthNote ? (
                  <p className="mb-2 text-[11px] leading-[1.5] text-lo">{d.strengthNote}</p>
                ) : null}
                <Button
                  data-testid={`otp-modal-destination-${d.method}`}
                  variant="secondary"
                  disabled={busy}
                  onClick={() => handleSend(d)}
                >
                  Send here
                </Button>
              </div>
            ))
          )}
        </div>
      )}

      {eligible && otpSent && (
        <div data-testid="otp-modal-readback">
          <p className="mb-2 text-[13px] text-mid">
            Enter the 6-digit code sent to the {otpMethod === 'email' ? 'email' : 'mobile number'} of record.
          </p>
          <input
            data-testid="otp-modal-code-input"
            className="mb-2 h-[34px] w-full rounded-[8px] border border-subtle bg-card px-2.5 text-[13px] text-hi"
            inputMode="numeric"
            maxLength={6}
            value={otpCode}
            onChange={(e) => set({ otpCode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
          />
          {mismatch && (
            <p data-testid="otp-modal-mismatch" className="mb-2 text-[12px] text-bad">
              That code didn't match. Try again.
            </p>
          )}
          <Button data-testid="otp-modal-verify-btn" disabled={busy || otpCode.length !== 6} onClick={handleVerify}>
            Verify
          </Button>
        </div>
      )}
    </Modal>
  )
}
